/**
 * Project records — the single source of truth for every project card and
 * detail page on the site.
 *
 * PRODUCT.md principle 1: never fabricate evidence. Every entry here is a
 * real project. The invented placeholder projects that used to pad this
 * list (Prairie Ridge, Hearth & Hollow, Cedar Bend) were removed in August
 * 2026 once there was a genuine second project to show. Do not add
 * imaginary projects to make the grid look fuller, and do not point an
 * entry at a stand-in photo — `image: null` is what makes a card show its
 * facts instead of pretending to have a rendering.
 */

export const projects = [
  {
    slug: 'founding-residence',
    title: 'The Founding Residence',
    /* Where the display heading breaks — a design decision, not something
       to derive from the string at render time. */
    titleLines: ['The Founding', 'Residence'],
    status: '2026',
    location: 'Normal, IL',

    /* Status labels shown beside the title on the project cards. Remove the
       'sale' badge when the home sells, and the 'building' one when it is
       finished — and change the Status spec row below at the same time, so
       the card and the spec list can't disagree. */
    badges: [
      { text: 'For Sale', tone: 'sale' },
    ],
    /* The quiet line under the title on the project cards. Just the date:
       'move-in' already says the house is not finished, so naming the
       state as well only spent words. 'Move-in' rather than 'completion'
       throughout — it is the milestone a buyer actually cares about, and
       it matches the last of the five process stages. Keep in step with
       the Est. move-in spec row below. */
    statusLine: 'Move-in November 2026',
    /* Small screens only. Replaces the statusLine and the label over the
       image, both of which are hidden at that size — one bar carrying both
       facts instead of two treatments competing for a narrow card. */
    statusBar: { left: 'For sale', right: 'November 2026 move-in', tone: 'sale' },
    eyebrow: 'Normal, Illinois · 2026',
    address: '619 Rose Xing, Normal, IL 61761',
    featured: true,
    image: '/images/rendering-01.jpg',
    imageAlt:
      'Rendering of a modern farmhouse custom home with cream board-and-batten siding, espresso-brown trim, dark garage doors and a charcoal shingle roof, set on a landscaped lot.',
    lede: 'Abode’s first home — drawn from a blank page, under construction now in Normal, and listed for sale. The standard every home after it gets measured against.',
    /* TODO: replace this draft with the real story of the home — what shaped
       the layout, the materials chosen and why. The "built for one family"
       framing used elsewhere on the site is deliberately absent here, since
       this home is built on spec and listed for sale rather than for a
       particular family. Put it back only if that changes.

       The copy below describes the design rather than a finished house,
       which is correct while it is under construction. Revisit the tense
       once it completes. */
    body: [
      'A two-storey modern farmhouse, drawn and detailed from scratch rather than pulled from a plan book. Cream board-and-batten siding runs the full height of the elevation, broken by espresso-brown trim and a tall glazed bay that carries daylight deep into the stair and the rooms behind it.',
      'The roofline steps down across the front of the house so the mass reads as something that grew over time rather than arriving all at once — a charcoal shingle field above, a covered entry and a three-bay garage tucked beneath. Every material on the exterior was chosen to be looked at closely, not just driven past.',
    ],
    /* Named as a rendering rather than left to look like a photograph. The
       house is still going up, so an unlabelled photo-realistic image
       implies a finished home that does not exist yet (PRODUCT.md,
       principle 1). Swap in real photography once there is some. */
    heroCaption:
      'Rendering — The Founding Residence, front elevation: cream board-and-batten siding, espresso-brown trim and a charcoal shingle roof.',
    specs: [
      { label: 'Style', value: 'Modern farmhouse' },
      { label: 'Stories', value: '2' },
      { label: 'Bedrooms', value: '5' },
      { label: 'Bathrooms', value: '4 full' },
      { label: 'Finished area', value: '3,881 sq ft' },
      { label: 'Lot size', value: '0.31 acres (approx.)' },
      // Garage bay count is still only read off the rendering, not confirmed.
      { label: 'Garage', value: '3 bays' },
      { label: 'Exterior', value: 'Board-and-batten' },
      { label: 'Status', value: 'Under construction · For sale' },
      { label: 'Est. move-in', value: 'November 2026' },
    ],
    // The public listing. Rendered as a link in the Specifications panel,
    // next to the "For sale" status row. Set this back to null the day the
    // house sells — the link disappears rather than going stale, and the
    // Status row above it should change at the same time.
    listingUrl:
      'https://www.zillow.com/homedetails/619-Rose-Xing-Normal-IL-61761/296241517_zpid/',
    /* Which Project Stage the contact form should preselect for someone
       arriving from this project. A project that is not a listed home
       wants a different one. */
    enquiryStage: 'listed-home',
    // TODO: add real images here, each { src, alt, caption }. Until then the
    // detail page renders honest placeholder tiles.
    gallery: [],
  },
  {
    slug: 'hershey-grove',
    title: 'Hershey Grove',
    location: 'Bloomington, IL',
    address: '1747 Eide Rd, Bloomington, IL 61704',
    /* A real project, not a placeholder: no renderings exist yet, but the
       location, lot and schedule below are confirmed. Everything here came
       from the builder — do not add specs (size, beds, baths, style) until
       they are settled, since a wrong figure on a real home is worse than
       no figure at all. */
    upcoming: true,
    image: null,
    /* "Available", not "For sale": there is no house to sell yet. What is
       on offer is the lot and a home designed on it, so the label has to
       promise the opportunity without implying a finished building. */
    badges: [{ text: 'Available', tone: 'available' }],
    statusBar: { left: 'Available', right: 'October 2027 move-in', tone: 'available' },
    /* Not "renderings coming soon" — that frames an empty panel as a
       delay. The plans are open because they are waiting for whoever
       buys, which is the selling point rather than a gap. */
    factsNote: 'Design in progress',
    facts: [
      { label: 'Lot size', value: '0.23 acres' },
      { label: 'Breaking ground', value: 'April 2027' },
      /* The status bar above the card states this on small screens, so the
         row stands down there rather than saying it twice. */
      { label: 'Est. move-in', value: 'October 2027', redundantOnMobile: true },
    ],
  },
];

/** Only projects with a real detail page written for them are linkable. */
export const isLinkable = (project) => Boolean(project.body);

export const getProject = (slug) => projects.find((p) => p.slug === slug);

export const featuredProjects = projects.slice(0, 3);
